import React, {useCallback, useEffect, useRef, useState, useImperativeHandle, forwardRef} from 'react';
import { createPortal } from 'react-dom';
import { useRafInterval } from 'ahooks';
import { useMarketplaceExperiment, useUserContext } from "../../contexts";
import { searchAssets, subscribeUserFn, resumeSubscriptionFn, meFn, getPurchasedBundlesFn, Analytics, deleteFaultyCookies } from '../../lib/api';
import useClickable from '../../hooks/useClickable';
import Menu from './Menu';
import Logo from './logo.svg';
import LogoName from './logo.name.svg';
import LogoIcon from './logo.icon.svg';
import { Spinner } from '../Spinner';
import Survey from '../Survey/Survey';
import Banner from './Banner';
import ProfilePicture from './ProfilePicture';
import SearchBar from './SearchBar';
import NotificationsBell from './NotificationsBell';
import {
    getHeaderDisplayEmail,
    getHeaderDisplayName,
    getProductioncrateSiteHref,
    isStripePaymentPastDue,
    shouldShowCreditCardUpgradeLink,
    shouldShowPaypalUpgradeLink,
    shouldShowUpgradeCta,
} from './accountIdentity';
import TermsConsentModal from '../TermsConsentModal/TermsConsentModal';
import SubscriptionIrregularityModal from '../SubscriptionIrregularityModal/SubscriptionIrregularityModal';
import PastDuePaymentModal from '../PastDuePaymentModal/PastDuePaymentModal';
import { MarketplaceCartLayer, useMarketplaceCart } from '../MarketplaceCart';
import {
    consumeMarketplaceCartReturnMarker,
} from '../../lib/marketplaceCart';
import configValues from '../../lib/config';
import { getMembershipLevelLabel, hasPremiumAssetsAccess } from '../../lib/subscriptionCapabilities';


import './Header.scss';
import s from '../Survey/Survey.module.scss';

import CATEGORIES from './categories.json';

const BASE_URL_MAP = {
    //'development': 'http://crate-core.com',
    'development': 'https://user-qa.productioncrate.com',
    'qa': 'https://user-qa.productioncrate.com',
    'production': 'https://user.productioncrate.com',
    'webapp': 'https://user.productioncrate.com'
}

const LOCAL_DEV_HOSTNAMES = ['localhost', '127.0.0.1'];

const isLocalDevelopmentHost = () => {
    const hostname = window.location.hostname || '';
    return LOCAL_DEV_HOSTNAMES.includes(hostname) || hostname.endsWith('.localhost');
};

const resolveBaseHref = ({ external, environment }) => {
    if (!external) {
        return '';
    }

    if (isLocalDevelopmentHost()) {
        return window.location.origin;
    }

    return BASE_URL_MAP[environment];
};

const getEnvironment = () => {
    if (window.location.hostname==='app.productioncrate.com') {
        //console.log("ENV", "webapp");
        return 'webapp';
    }
    let env;
    if (window.location.hostname === "localhost" || window.location.hostname.includes("crate-webapp.pages.dev")){
        env = 'development';
    } else if (window.location.hostname.includes("user-qa") ||   window.location.hostname.includes("staging")) {
        env = 'qa';
    } else {
        env = 'production';
    }

    return env;
};
const env = getEnvironment();
const ACCOUNT_MENU_VIEWPORT_MARGIN = 12;

const normalizeAppToken = (value) => String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');

const resolveAvailableAppsFromPurchasedEntries = (entries = []) => {
    const appsByKey = new Map();

    (Array.isArray(entries) ? entries : []).forEach((entry) => {
        const appLists = [
            entry?.bundle?.apps,
            entry?.apps,
            entry?.bundle?.plugins,
            entry?.plugins,
        ];

        appLists.forEach((appList) => {
            (Array.isArray(appList) ? appList : []).forEach((appCandidate) => {
                const appObject = appCandidate && typeof appCandidate === 'object'
                    ? appCandidate
                    : { name: String(appCandidate ?? '').trim() };
                const appKey = [
                    appObject?.id ? `id:${String(appObject.id).trim()}` : '',
                    appObject?.slug ? `slug:${normalizeAppToken(appObject.slug)}` : '',
                    appObject?.app_slug ? `slug:${normalizeAppToken(appObject.app_slug)}` : '',
                    appObject?.appSlug ? `slug:${normalizeAppToken(appObject.appSlug)}` : '',
                    appObject?.name ? `name:${normalizeAppToken(appObject.name)}` : '',
                    appObject?.title ? `title:${normalizeAppToken(appObject.title)}` : '',
                ].find((value) => !!value);

                if (!appKey) {
                    return;
                }
                if (!appsByKey.has(appKey)) {
                    appsByKey.set(appKey, appObject);
                }
            });
        });
    });

    return Array.from(appsByKey.values());
};

const isGlobalSessionReady = (session) => Boolean(session?.analytics) && session?.availableAppsLoaded === true;


const Header = ({minimal=false, external=false, newUrlScheme=true, searchCallback='testSearch'}, ref) => {

    const headerRef = useRef(null);
    const searchInputRef = useRef(null);
    const accountMenuAnchorRef = useRef(null);
    const accountMenuCloseTimerRef = useRef(null);

    const urlScheme = ['development', 'qa'].includes(env) || newUrlScheme;

    const {user, unloadUser, focusMode, isAuthenticating, assetsAvailable, reloadCurrentUser} = useUserContext();
    const {
        isResolved: isMarketplaceResolved,
        marketplaceEnabled,
        resolvedCohort: marketplaceCohort,
    } = useMarketplaceExperiment();
    const {
        isEnabled: marketplaceCartEnabled,
        snapshot: cartSnapshot,
        controller: marketplaceCartController,
    } = useMarketplaceCart();

    const [searchQuery, setSearchQuery] = useState('');

    const [loading, setLoading] = useState(false);

    const [floatingVisible, setFloatingVisible] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);
    const [selectorFocus, setSelectorFocus] = useState(false);
    const [categorySelectorExpanded, setCategorySelectorExpanded] = useState(false);
    const [categorySelected, setCategorySelected] = useState('vfx');
    const [categoryByUrl, setCategoryByUrl] = useState('vfx');
    const [headerHeight, setHeaderHeight] = useState(125);
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    const [accountMenuPosition, setAccountMenuPosition] = useState({
        right: ACCOUNT_MENU_VIEWPORT_MARGIN,
        top: 0,
    });
    
    const [currentUrl, setCurrentUrl] = useState(window?.location?.href??'');
    
    const baseUrl = BASE_URL_MAP[env];
    const baseHref = resolveBaseHref({ external, environment: env });


    const showBrandSubtitle = ['rendercrate', 'footagecrate', 'graphicscrate', 'soundscrate']
    .some(domain => window.location.hostname.includes(domain));
    const accountDisplayName = getHeaderDisplayName(user);
    const accountDisplayEmail = getHeaderDisplayEmail(user);
    const userLevelLabel = String(getMembershipLevelLabel(user?.team?.subscription?.plan) || '').trim();
    const hasPremiumAssetDownloads = hasPremiumAssetsAccess(user?.team?.subscription?.plan);
    const premiumAssetsPolicy = hasPremiumAssetDownloads;
    const isPaymentPastDue = isStripePaymentPastDue(user);
    const showUpgradeCta = shouldShowUpgradeCta({ hasPremiumAssetDownloads, user });
    const showPaypalUpgradeLink = shouldShowPaypalUpgradeLink(user);
    const showCreditCardUpgradeLink = shouldShowCreditCardUpgradeLink(user);
    const shouldRenderMarketplaceCartLayer = marketplaceCartEnabled && !hasPremiumAssetDownloads && !isPaymentPastDue;
    const myAssetsHref = getProductioncrateSiteHref({ environment: env, pathname: '/my-assets' });


    const [surveyVisible, setSurveyVisible] = useState(false);
    const [thankYou, setThankYou] = useState(false);
    const [showUserProfileBanner, setShowUserProfileBanner] = useState(false);
    const [usedForText, setUsedForText] = useState('');
    const [notificationIndicator, setNotificationIndicator] = useState({
        badgeText: '',
        hasUnread: false,
    });
    const [availableApps, setAvailableApps] = useState([]);
    const [availableAppsLoaded, setAvailableAppsLoaded] = useState(false);
    const areAnnouncementsEnabled = configValues.announcementsEnabled;

    const [answered, setAnswered] = useState(0);
    const [surveyData, setSurveyData] = useState({
        thankYou: 'Thanks, your profile is 100% complete! Time to make it awesome!',
        name: "how_you_found_us",
        flagName: "crate.surveyFlagId1",
        questions: [
          {
            labelAccent: "1 of 3",
            label: "What is your profession?",
            value: 'profession',
            cta: "Next",
            answers: [
                {
                  label: "🎥 Vlogger",
                  value: 'vlogger',
                  selected: false,
                },
                {
                  label: "💼 Business owner",
                  value: 'business-owner',
                  selected: false,
                },
                {
                  label: "🎨 VFX artist",
                  value: 'vfx-artist',
                  selected: false,
                },
                {
                  label: "🖌️ Motion graphics artist",
                  value: 'motion-graphics-artist',
                  selected: false,
                },
                {
                  label: "📚 Teacher",
                  value: 'teacher',
                  selected: false,
                },
                {
                  label: "🎓 Student",
                  value: 'student',
                  selected: false,
                },
                {
                  label: "✂️ Video editor",
                  value: 'video-editor',
                  selected: false,
                },
                {
                  label: "🎬 Filmmaker",
                  value: 'filmmaker',
                  selected: false,
                },
                {
                  label: "💻 Developer",
                  value: 'developer',
                  selected: false,
                },
                {
                  label: "❓ Other",
                  value: 'other',
                  selected: false,
                },
              ]
          },
          {
            labelAccent: "2 of 3",
            label: "What software do you use?",
            value: 'software_preferences',
            cta: "Next",
            answers: [
                {
                    label: "After Effects",
                    value: "after-effects",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/after_effects.svg"
                },
                {
                    label: "iMovie",
                    value: "imovie",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/imovie.svg"
                },
                {
                    label: "Adobe Premiere",
                    value: "adobe-premiere",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/premiere_pro.svg"
                },
                {
                    label: "DaVinci Resolve",
                    value: "davinci-resolve",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/davinci.svg"
                },
                {
                    label: "Final Cut",
                    value: "final-cut",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/final_cut.svg"
                },
                {
                    label: "Wondershare Filmora",
                    value: "wondershare-filmora",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/filmora.svg"
                },
                {
                    label: "Sony Vegas",
                    value: "sony-vegas",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/sony_vegas.svg"
                },
                {
                    label: "BlackMagic Fusion",
                    value: "blackmagic-fusion",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/blackmagic_fusion.png"
                },
                {
                    label: "Kinemaster",
                    value: "kinemaster",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/kine_master.svg"
                },
                {
                    label: "Nuke",
                    value: "nuke",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/nuke.svg"
                },
                {
                    label: "Blender",
                    value: "blender",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/blender.svg"
                },
                {
                    label: "Power Director",
                    value: "power-director",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/power_director.png"
                },
                {
                    label: "Photoshop",
                    value: "photoshop",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/photoshop.svg"
                },
                {
                    label: "CapCut",
                    value: "capcut",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/capcut.png"
                },
                {
                    label: "Cinema 4D",
                    value: "cinema-4d",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/cinema4d.png"
                },
                {
                    label: "Houdini",
                    value: "houdini",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/houdini.png"
                },
                {
                    label: "Maya",
                    value: "maya",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/maya.png"
                },
                {
                    label: "Unity",
                    value: "unity",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/unity.svg"
                },
                {
                    label: "Unreal Engine",
                    value: "unreal-engine",
                    selected: false,
                    icon: "https://user.productioncrate.com/images/logos/unreal_engine.png"
                },
                {
                    label: "Other",
                    value: "other",
                    selected: false,
                    icon: null
                }
            ]
          },
          {
            labelAccent: "3 of 3",
            label: "What is your usage purpose?",
            value: 'usage_purpose',
            cta: "Send",
            answers: [
                { label: "Not Sure", value: "not-sure", selected: false },
                { label: "School Project", value: "school-project", selected: false },
                { label: "Films", value: "feature-film", selected: false },
                { label: "Commercials", value: "client-projects-commercials", selected: false },
                { label: "YouTube", value: "youtube", selected: false },
                { label: "Just exploring!", value: "just-browsing", selected: false },
                { label: "Games", value: "games", selected: false },
                { label: "Instagram", value: "instagram", selected: false },
                { label: "TikTok", value: "tiktok", selected: false },
                { label: "Music Videos", value: "music-videos", selected: false },
                { label: "Graphics", value: "graphic-design", selected: false },
                { label: "VFX Shots", value: "vfx-shots", selected: false },
                { label: "Other Socials", value: "other-socials", selected: false },
                { label: "Learning/Practice", value: "learning-practice", selected: false },
                { label: "Other", value: "other", selected: false }
            ]
          }
        ],
    });
    const submitAnswer = (j, i) => {
        //surveyData.questions[j].answers[i].selected = true;
        console.log("SURVEY", j, i, surveyData.questions[j].answers[i]);
        setSurveyData(prev => {
            let newData = {...prev};
            newData.questions[j].answers[i].selected = !newData.questions[j].answers[i].selected;

            console.log(newData.questions[j].answers[i]);
            return newData;
        });
    };


    const clearInterval = useRafInterval(() => {
        let path = window?.location?.pathname || '';
        if (currentUrl && currentUrl !== window.location.href) {
            console.log("HEADER HREF > currentUrl", currentUrl, "new", window.location.href);
            setCurrentUrl(window.location.href);
        }
        if (path.includes('/search')) {


            const urlParams = new URLSearchParams(window.location.search);
            const mainCategory = urlParams.get('main_category');
    
            if (['all', 'vfx', 'sfx', '3d', 'graphics'].some(c => c === mainCategory)) {
                setCategoryByUrl(mainCategory);
            }
        }
    }, 100);

    useEffect(() => {
        // Cleanup the RAF interval
        return () => {
          clearInterval()
        }
    }, [clearInterval]);

    const hasFixedBottomBanner = !!user && (
        user.email_verified === false ||
        (user.email_verified && showUserProfileBanner)
    );

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }
        const body = document.body;
        if (!body) {
            return;
        }

        let rafId = null;
        const setBottomBannerOffset = () => {
            const banners = document.querySelectorAll('.header .banner');
            let maxHeight = 0;
            banners.forEach((banner) => {
                const rect = banner.getBoundingClientRect();
                maxHeight = Math.max(maxHeight, rect.height || 0);
            });
            body.style.setProperty('--fixed-bottom-banner-offset', `${Math.ceil(maxHeight)}px`);
        };

        if (hasFixedBottomBanner) {
            body.classList.add('has-fixed-bottom-banner');
            rafId = window.requestAnimationFrame(setBottomBannerOffset);
            window.addEventListener('resize', setBottomBannerOffset);
        } else {
            body.classList.remove('has-fixed-bottom-banner');
            body.style.setProperty('--fixed-bottom-banner-offset', '0px');
        }

        return () => {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
            }
            window.removeEventListener('resize', setBottomBannerOffset);
            body.classList.remove('has-fixed-bottom-banner');
            body.style.setProperty('--fixed-bottom-banner-offset', '0px');
        };
    }, [hasFixedBottomBanner]);
    
    useEffect(() => {
        setCategorySelected(categoryByUrl);
    }, [categoryByUrl, setCategorySelected]);
    

    useEffect(() => {
        //this is for cleaning up an old error with cookies. Should disppear from the code soon...
        deleteFaultyCookies();

        if (!window.location.pathname.includes('/search')){
            return;
        }

        let searchTerm = '';
        
        const parts = window.location.pathname.split('/');
        const urlParams = new URLSearchParams(window.location.search);
        const mainCategory = urlParams.get('main_category');


        if (parts[1] === 'search' && parts[2]) {
            searchTerm = decodeURIComponent(parts[2]);
        } else if(parts[1] === 'search' && !parts[2]){
            searchTerm = '';
        }
        searchTerm = searchTerm.replace('+', ' ');
        if (searchTerm.length>0){
            console.log("HEADER RESOLUTION > search_query", searchTerm);
            setSearchQuery(searchTerm);
        }

        if (!mainCategory && window.location.host.includes('footagecrate')){
            setCategorySelected('vfx');
        }
        if (!mainCategory && window.location.host.includes('rendercrate')){
            setCategorySelected('3d');
        }
        if (!mainCategory && window.location.host.includes('graphicscrate')){
            setCategorySelected('graphics');
        }
        if (!mainCategory && window.location.host.includes('soundscrate')){
            setCategorySelected('sfx');
        }

        if (['all', 'vfx', 'sfx', '3d', 'graphics'].some(c => c === mainCategory)) {
            console.log("HEADER RESOLUTION > category", mainCategory);
            setCategorySelected(mainCategory);
        }
        if (searchTerm.length>0 || ['vfx', 'sfx', '3d', 'graphics'].some(c => c === mainCategory)) {
            setSearchFocused(true);
            setTimeout(() => {
                if (searchInputRef && searchInputRef.current) {
                    searchInputRef.current.focus();
                }
            }, 100);
        }
        setTimeout(() => {
            if (searchInputRef && searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }, 100);

        if (!window.debugDomainHeader) {
            window.debugDomainHeader = () => {
                console.log("DEBUG DOMAIN");
                console.log({baseUrl, baseHref, env, external, newUrlScheme});
            };
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const updateHeaderHeight = () => {
            const nextHeight = Math.ceil(headerRef.current?.getBoundingClientRect?.().height || 125);
            setHeaderHeight(previousHeight => previousHeight === nextHeight ? previousHeight : nextHeight);
        };

        updateHeaderHeight();

        if (typeof window.ResizeObserver === 'function' && headerRef.current) {
            const resizeObserver = new window.ResizeObserver(updateHeaderHeight);
            resizeObserver.observe(headerRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }

        window.addEventListener('resize', updateHeaderHeight);

        return () => {
            window.removeEventListener('resize', updateHeaderHeight);
        };
    }, []);

    useEffect(() => {
        try{
            Analytics.view('page', {target: window.location.href});
        } catch(e){
            console.log("Error while sending page view", e);
        }
    }, [currentUrl]);
    useEffect(() => {
        if (!user) return;
        console.log("SETTINGS", user.settings);
        const skipUserProfile = user.settings?.skipUserProfile;
        if (skipUserProfile) {
            return;
        }
        const profession = user.settings?.profession;
        const software_preferences = user.settings?.software_preferences;
        const usage_purpose = user.settings?.usage_purpose;

        if (profession && software_preferences && usage_purpose) {
            return;
        }

        if (profession && !software_preferences && !usage_purpose) setAnswered(1);
        if (profession && software_preferences && !usage_purpose) setAnswered(2);

        const previousAnswers = [profession, software_preferences, usage_purpose];
        setSurveyData(prev => {
            let newData = {...prev};
            previousAnswers.forEach((quest,i) => {
                //console.log("quest", quest);
                if (!quest) return;
                if (Array.isArray(quest)) {
                    newData.questions[i].answers.forEach(ans => {
                        ans.selected = quest.includes(ans.value);
                        if(ans.selected) console.log("ans in array", ans, quest);
                    });
                }
                if (typeof quest === 'string') {
                    newData.questions[i].answers.forEach(ans => {
                        ans.selected = ans.value === quest.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                        if(ans.selected) console.log("ans as string", ans, quest);
                    });
                }
            });

            return newData;    
        });
        
        setTimeout(() => setShowUserProfileBanner(true), 2000);
        

        
    }, [user]);


    useEffect(() => {
        let mounted = true;

        if (isAuthenticating) {
            return () => {
                mounted = false;
            };
        }

        if (!user) {
            setAvailableApps([]);
            setAvailableAppsLoaded(true);
            return () => {
                mounted = false;
            };
        }

        setAvailableAppsLoaded(false);
        getPurchasedBundlesFn()
            .then((purchasedEntries) => {
                if (!mounted) return;
                setAvailableApps(resolveAvailableAppsFromPurchasedEntries(purchasedEntries));
            })
            .catch((error) => {
                if (!mounted) return;
                console.log("Error loading available apps from purchased bundles", error);
                setAvailableApps([]);
            })
            .finally(() => {
                if (!mounted) return;
                setAvailableAppsLoaded(true);
            });

        return () => {
            mounted = false;
        };
    }, [user, isAuthenticating]);

    useEffect(() => {
        if (isAuthenticating) return;

        
        const runCallback = () => {
            const session = {
                user,
                availableApps: user ? availableApps : [],
                availableAppsLoaded,
                analytics: Analytics,
                marketplace: {
                    cohort: marketplaceCohort,
                    enabled: marketplaceEnabled,
                    isResolved: isMarketplaceResolved,
                },
            };
            window.getGlobalSession = () => session;

            if (typeof window.waitForGlobalSession !== 'function') {
                window.waitForGlobalSession = () => {
                    const currentSession = typeof window.getGlobalSession === 'function' ? window.getGlobalSession() : null;
                    if (isGlobalSessionReady(currentSession)) {
                        return Promise.resolve(currentSession);
                    }

                    return new Promise((resolve) => {
                        const handleSessionEvent = (event) => {
                            if (!isGlobalSessionReady(event?.detail)) {
                                return;
                            }
                            window.removeEventListener('crate:header:session', handleSessionEvent);
                            window.removeEventListener('crate:header:ready', handleSessionEvent);
                            resolve(event.detail);
                        };

                        window.addEventListener('crate:header:session', handleSessionEvent);
                        window.addEventListener('crate:header:ready', handleSessionEvent);
                    });
                };
            }

            window.dispatchEvent(new CustomEvent('crate:header:session', { detail: session }));
            if (!window.__crateHeaderReady && isGlobalSessionReady(session)) {
                window.__crateHeaderReady = true;
                window.dispatchEvent(new CustomEvent('crate:header:ready', { detail: session }));
            }

            if (window.globalSessionCallback && typeof window.globalSessionCallback === 'function') {
                window.globalSessionCallback(user, Analytics);
            }
        };
    
        if (document.readyState === "complete") {
            runCallback();
        } else {
            window.addEventListener("load", runCallback, { once: true });
        }
    }, [user, availableApps, availableAppsLoaded, isAuthenticating, isMarketplaceResolved, marketplaceEnabled, marketplaceCohort]);

    useEffect(() => {
        if (!shouldRenderMarketplaceCartLayer && cartSnapshot.isOpen) {
            marketplaceCartController.close();
        }
    }, [cartSnapshot.isOpen, marketplaceCartController, shouldRenderMarketplaceCartLayer]);

    useEffect(() => {
        if (!marketplaceCartEnabled) {
            return;
        }

        marketplaceCartController.setSession({
            user,
            isAuthenticating,
        });
    }, [isAuthenticating, marketplaceCartController, marketplaceCartEnabled, user]);

    useEffect(() => {
        if (!isMarketplaceResolved) {
            return;
        }

        const hasReturnMarker = consumeMarketplaceCartReturnMarker(window);
        if (!hasReturnMarker || !marketplaceCartEnabled || hasPremiumAssetDownloads) {
            return;
        }

        marketplaceCartController.open();
    }, [
        hasPremiumAssetDownloads,
        isMarketplaceResolved,
        marketplaceCartController,
        marketplaceCartEnabled,
    ]);

    useEffect(() => {
        if (answered && answered>0) {
            const data = {};
            data[surveyData.questions[answered-1].value] = surveyData.questions[answered-1].answers.filter(a => a.selected).map(a => a.value);
            meFn(data);
        }
        if (answered === 3) {
            setThankYou(surveyData.thankYou);
        }
    }, [answered]);

    

    const handleCloseUserProfileSurvey = () => {
        setShowUserProfileBanner(false);
        setSurveyVisible(false);
        const data = {
            skipUserProfile: answered>=3
        };
        if (usedForText) {
            data.usedForText = usedForText;
        }
        meFn(data);
    };


    const handleLogout = async () => {
        if (marketplaceCartEnabled) {
            await marketplaceCartController.prepareForLogout();
        }

        // If we're not on user.productioncrate.com, redirect to the logout page there
        if (window.location.hostname === "staging.productioncrate.com"){
            window.location.href = "https://user-qa.productioncrate.com/logout";
            return;
        }
        if (window.location.hostname !== "user.productioncrate.com" && window.location.hostname !== "user-qa.productioncrate.com") {
            window.location.href = "https://user.productioncrate.com/logout";
            return;
        }
        unloadUser();
    };

    const handleNotificationIndicatorChange = useCallback((indicator) => {
        const nextHasUnread = Boolean(indicator?.hasUnread);
        const nextBadgeText = nextHasUnread ? `${indicator?.badgeText || ''}` : '';

        setNotificationIndicator((previousState) => {
            if (
                previousState.hasUnread === nextHasUnread &&
                previousState.badgeText === nextBadgeText
            ) {
                return previousState;
            }

            return {
                badgeText: nextBadgeText,
                hasUnread: nextHasUnread,
            };
        });
    }, []);

    const clearAccountMenuCloseTimer = useCallback(() => {
        if (accountMenuCloseTimerRef.current) {
            window.clearTimeout(accountMenuCloseTimerRef.current);
            accountMenuCloseTimerRef.current = null;
        }
    }, []);

    const updateAccountMenuPosition = useCallback(() => {
        const anchor = accountMenuAnchorRef.current;
        if (!anchor || typeof window === 'undefined') {
            return;
        }

        const rect = anchor.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;

        setAccountMenuPosition({
            top: Math.max(ACCOUNT_MENU_VIEWPORT_MARGIN, Math.round(rect.bottom + 8)),
            right: Math.max(ACCOUNT_MENU_VIEWPORT_MARGIN, Math.round(viewportWidth - rect.right)),
        });
    }, []);

    const openAccountMenu = useCallback(() => {
        clearAccountMenuCloseTimer();
        updateAccountMenuPosition();
        setIsAccountMenuOpen(true);
    }, [clearAccountMenuCloseTimer, updateAccountMenuPosition]);

    const scheduleCloseAccountMenu = useCallback(() => {
        clearAccountMenuCloseTimer();
        accountMenuCloseTimerRef.current = window.setTimeout(() => {
            setIsAccountMenuOpen(false);
            accountMenuCloseTimerRef.current = null;
        }, 120);
    }, [clearAccountMenuCloseTimer]);

    const handleAccountMenuBlur = useCallback(() => {
        scheduleCloseAccountMenu();
    }, [scheduleCloseAccountMenu]);

    useEffect(() => () => {
        clearAccountMenuCloseTimer();
    }, [clearAccountMenuCloseTimer]);

    useEffect(() => {
        if (!isAccountMenuOpen) {
            return undefined;
        }

        updateAccountMenuPosition();
        window.addEventListener('resize', updateAccountMenuPosition);
        window.addEventListener('scroll', updateAccountMenuPosition, true);

        return () => {
            window.removeEventListener('resize', updateAccountMenuPosition);
            window.removeEventListener('scroll', updateAccountMenuPosition, true);
        };
    }, [isAccountMenuOpen, updateAccountMenuPosition]);


    const reactivateUser = (provider, planId) => {
        setLoading(true);
        let postData = {provider, planId};
        
        resumeSubscriptionFn(postData).then(data => {
            if(data && data.url){
                document.location.href = data.url;
                return;
            }
            // No redirect: just refresh user state
            try { reloadCurrentUser && reloadCurrentUser(); } catch(e) {}
            setLoading(false);
        })
        .catch(e => {
            console.log("Problem while subscribing user tc", e);
            setLoading(false);
            try { reloadCurrentUser && reloadCurrentUser(); } catch(_) {}
            //document.location.href = '/register';
        });

        

    };

    const accountSubmenu = isAccountMenuOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
                className="account-menu-submenu is-open"
                id="js-account-menu"
                onMouseEnter={openAccountMenu}
                onMouseLeave={scheduleCloseAccountMenu}
                onFocus={openAccountMenu}
                onBlur={handleAccountMenuBlur}
                style={{
                    '--account-menu-top': `${accountMenuPosition.top}px`,
                    right: `${accountMenuPosition.right}px`,
                    top: `${accountMenuPosition.top}px`,
                }}
            >
                {user &&
                <div className="account-identity">
                    <ProfilePicture showPremiumStar={hasPremiumAssetDownloads} user={user} />
                    <div className="account-identity__text">
                        <div className="account-identity__name">{accountDisplayName}</div>
                        {accountDisplayEmail &&
                        <div className="account-identity__email">{accountDisplayEmail}</div>
                        }
                    </div>
                </div>
                }
                {assetsAvailable !== null && user &&
                <div className="item head">
                    {assetsAvailable} downloads remaining today.
                </div>
                }
                {userLevelLabel && user &&
                <div className="item head">
                    User Level: {userLevelLabel}
                </div>
                }
                {areAnnouncementsEnabled &&
                <NotificationsBell onIndicatorChange={handleNotificationIndicatorChange} />
                }
                {user &&
                <a className="item show-logged-free show-logged-pro" href={`${baseHref}/account`}>
                    <i className="glyphicon glyphicon-user"></i>
                    Member Page
                </a>
                }
                {user &&
                <a className="item show-logged-free show-logged-pro" href={myAssetsHref}>
                    <i className="glyphicon glyphicon-image"></i>
                    My Assets
                </a>
                }
                {!user &&
                <>
                    <a className="item show-logged-out" href={`${baseHref}/login`}>
                        <i className="glyphicon glyphicon-user"></i>
                        Login
                    </a>
                    <a className="item show-logged-out" href={`${baseHref}/register`}>
                        <i className="glyphicon glyphicon-lock"></i>
                        Register
                    </a>
                    {false &&
                    <a className="item show-logged-out" href={`${baseHref}/register`}>
                        <i className="glyphicon glyphicon-facebook-square"></i>
                        Facebook Connect
                    </a>
                    }
                    <a className="item show-logged-out" href={`${baseHref}/register`}>
                        <i className="glyphicon glyphicon-google-plus-square"></i>
                        Google Connect
                    </a>
                </>
                }
                {!premiumAssetsPolicy &&
                <>
                    {showPaypalUpgradeLink &&
                    <a className="item show-logged-free" href={`${baseHref}/upgrade/?prov=p`}>
                        <i className="glyphicon glyphicon-paypal"></i>
                        PRO Upgrade (PayPal)
                    </a>
                    }
                    {showCreditCardUpgradeLink &&
                    <a className="item show-logged-free" href={`${baseHref}/upgrade`}>
                        <i className="glyphicon glyphicon-credit-card"></i>
                        PRO Upgrade (credit/Debit)
                    </a>
                    }
                </>
                }
                {user &&
                <div className="item show-logged-free show-logged-pro" onClick={handleLogout}>
                    <i className="glyphicon glyphicon-lock"></i>
                    Logout
                </div>
                }
            </div>,
            document.body
        )
        : null;

    return (
        <div className={`header ${focusMode?'fade-out':''} ${minimal?'minimal':''} `} ref={headerRef}>
            {!minimal &&
            <>
                {accountSubmenu}
                <Survey />
                <Banner />
                <TermsConsentModal />
                <SubscriptionIrregularityModal />
                <PastDuePaymentModal />
                {shouldRenderMarketplaceCartLayer &&
                <MarketplaceCartLayer
                    controller={marketplaceCartController}
                    snapshot={cartSnapshot}
                    topOffset={headerHeight}
                />
                }
            </>
            }
            <div className="first-row header-nav-row" style={{paddingBottom: '4px'}}>
                <div className="wrapper" style={{display: 'flex'}}>
                    <a className="logo" href="https://www.productioncrate.com">
                        <img className="pic" src={`https://user.productioncrate.com${Logo}`} alt="Logo" width="42" height="42" />
                        <div className="name">
                            <img src={`https://user.productioncrate.com${LogoName}`} alt="logo name" width="224" height="33" />
                            {showBrandSubtitle &&
                            <div className="brand_subtitle">
                                by ProductionCrate
                            </div>
                            }
                        </div>
                    </a>
                    {!minimal &&
                    <div className="wrapper wrapper_menu" style={{alignItems: 'center', flexDirection: 'column', justifyContent: 'space-between', flexWrap: 'wrap'}}>
                        <Menu visible={floatingVisible} closeCallback={() => setFloatingVisible(false)} onLogout={handleLogout} external={external} newUrlScheme={urlScheme} />
                    </div> 
                    }
                    {!minimal && !user && !isAuthenticating &&
                    <div className="controls">
                        {env === 'development' &&
                        <a className="item show-logged-out" href={`/login`}>Login</a>
                        }
                        {env !== 'development' &&
                        <a className="item show-logged-out" href={`${baseHref}/login`}>Login</a>
                        }
                        <a className="item show-logged-out accent" href={`${baseHref}/register`}>Register</a>
                    </div>
                    }
                    {!minimal && user &&
                    <div className="account-menu">
                        {!premiumAssetsPolicy &&
                        <div className="controls long-desktop_only" style={{width: 'auto'}}>
                            {false && user.team.last_paid_subscription && user.team.last_paid_subscription.id != user.team.subscription?.id && user.team.last_paid_subscription.stripe === true && 
                            <div className="item show-logged-free accent" style={{width: 'max-content', cursor: 'pointer'}} onClick={() => reactivateUser('stripe', user.team.last_paid_subscription.plan.id)}>
                                Resume Subscription
                            </div>
                            }
                            {showUpgradeCta &&
                            <a className="item show-logged-free accent" href={`${baseHref}/upgrade`}>
                                <span className="label-full">Get unlimited access</span>
                                <span className="label-compact">Get unlimited access</span>
                            </a>
                            }
                        </div>
                        }
                        <div
                            className={`user-data ${showUpgradeCta ? 'has-upgrade-cta' : ''}`}
                            ref={accountMenuAnchorRef}
                            onMouseEnter={openAccountMenu}
                            onMouseLeave={scheduleCloseAccountMenu}
                            onFocus={openAccountMenu}
                            onBlur={handleAccountMenuBlur}
                        >
                            {user && 
                                <div className="username" >
                                    {accountDisplayName}
                                </div>
                            }
                            {!user && 
                                <div className="username" >
                                    Account
                                </div>
                            }
                            {/*<ProfilePicture user={user} progress={100*(answered + (surveyData.questions[answered]?.answers.find(a => a.selected)?1:0) )/surveyData.questions.length} />*/}
                            <ProfilePicture
                                user={user}
                                notificationBadgeText={areAnnouncementsEnabled && notificationIndicator.hasUnread ? notificationIndicator.badgeText : ''}
                                showPremiumStar={hasPremiumAssetDownloads}
                            />
                            
                        </div>
                    </div>
                    }
                    {!minimal && !user && isAuthenticating &&
                    <div className="account-menu">
                        Loading user...
                    </div>
                    }
                    {!minimal && !premiumAssetsPolicy && user &&
                    <div className="controls mobile_only mobile-upgrade-controls" style={{justifyContent: 'center'}}>
                        {false && user.team.last_paid_subscription && user.team.last_paid_subscription.id != user.team.subscription?.id && user.team.last_paid_subscription.stripe === true && 
                        <div className="item show-logged-free accent" style={{width: 'max-content', cursor: 'pointer', minWidth: '240px'}} onClick={() => reactivateUser('stripe', user.team.last_paid_subscription.plan.id)}>
                            Resume Subscription
                        </div>
                        }
                        {showUpgradeCta &&
                        <>
                            <a className="item show-logged-free accent" style={{cursor: 'pointer'}} href={`${baseHref}/upgrade`}>
                                Get unlimited access
                            </a>
                        </>
                        }
                    </div>
                    }
                    {!minimal &&
                        <button type="button" className="menu-toggle" aria-label="Open navigation menu" aria-expanded={floatingVisible} onClick={() => setFloatingVisible(true)}>
                            <img alt="" src={`${baseHref}/images/icon.burger.svg`} />
                        </button>
                    }
                </div>
            </div>
            {!minimal && 
            <div className="first-row header-search-row" style={{paddingTop: '4px'}}>
                <div className="wrapper" style={{display: 'flex'}}>
                {!minimal && 
                <div style={{position: 'relative',display: 'flex', flexDirection:'row', alignItems: 'center', gap: '12px', width: '100%'}} className={``}>
                    <SearchBar searchCallback={searchCallback} />
                </div>
                }
                </div>
            </div>
            }
            {!minimal && user && user.email_verified === false &&
            <div className="banner">
                <div className='content' onClick={() => document.location.href='https://user.productioncrate.com/member/email-verify'}>
                Action Required: Your email address has not been verified yet. Click <a href='https://user.productioncrate.com/member/email-verify'>here</a> to resend the verification email
                </div>
            </div>
            }
            {/* Let's hide the banner for the moment until release finish */}
            {!minimal && user && user.email_verified && showUserProfileBanner &&
            <>
                <div className="banner" style={{backgroundColor: '#ea5317'}}>
                    <div className='content' style={{width: 'max-content'}} onClick={() => setSurveyVisible(true)}>
                    Your profile is almost complete - Click here to finish!
                    </div>
                </div>
                <div className={`${s.survey} ${surveyVisible?s.visible:''}`}>
                    <div className={`${s.shadow}`} onClick={handleCloseUserProfileSurvey}/>
                    <div className={`${s.modal}`}>
                        <span className={`${s.close} `} onClick={handleCloseUserProfileSurvey}>X</span>
                        <div className={`${s.progress_bar}`}>
                            {answered<3 && 
                            <div className={`${s.progress}`} style={{width: `${100*(answered + (surveyData.questions[answered].answers.find(a => a.selected)?1:0) )/surveyData.questions.length}%`}}/>
                            }
                            {answered<=3 && 
                            <div className={`${s.progress}`} style={{width: `100%`}}/>
                            }
                        </div>
                        {!thankYou && answered<3 && surveyData.questions.filter((q, qIndex) => qIndex === answered ).map((question) =>
                        <React.Fragment key={`question-index-${answered}`}>
                            <div className={`${s.question} ${s.accent} m-b-16`}>{question.labelAccent}</div>
                            <div className={`${s.question} m-b-16`}>{question.label}</div>
                            <div className={`${s.column_wrapper}`}>
                            {question.answers.map((answer, aIndex) => 
                                <div key={`question-${answered}-answer-${aIndex}`} id={`question-1-answer-${aIndex}`} style={{display: 'flex', alignItems: 'center', gap: '8px', minWidth: '240px'}} className={`${s.answer} ${answer.selected?s.selected:''} m-b-8`} onClick={() => submitAnswer(answered, aIndex)}>
                                    {answer.icon &&
                                    <img style={{maxWidth: '25px', height: 'auto'}} src={answer.icon} alt="software logo" /> 
                                    }
                                    {answer.label}
                                </div>
                            )}
                            </div>
                            <div className={`${s.cta} ${question.answers.find(a => a.selected)?'':s.disabled}`} onClick={() => setAnswered(prev => prev+1)}>{question.cta}</div>
                            {answered>0 && 
                            <div className={`${s.back} m-t-12 m-h-a`} onClick={() => setAnswered(prev => prev-1)}>Back</div>
                            }
                            {answered===0 && 
                            <div className={`${s.back} m-t-12 m-h-a`} ></div>
                            }
                        </React.Fragment>
                        )}
                        {thankYou && 
                        <>
                            <div className={`${s.thankYou} ${s.accent} m-b-16`}>{thankYou}</div>
                            <div style={{ margin: '24px 0 12px 0', textAlign: 'center'}}>We would love to see what you have built with our assets:</div>
                            <textarea value={usedForText} onChange={(e) => setUsedForText(e.target.value)} placeholder="URL or description" style={{ height: "90px", width: "435px", padding: "8px", color: "white", background: "#1e1d1d" }} />
                            <div className={`${s.cta}`} onClick={handleCloseUserProfileSurvey}>Save & Close</div>
                        </>
                        }
                    </div>
                </div>
            </>
            }
            {!minimal && loading &&
            <div style={{background: 'rgba(0,0,0,0.9)', width: '100vw', height: '100vh', top: '0', left: '0', position: 'fixed', zIndex: '9', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Spinner label="Reactivating subscription..." />
            </div>
            }
            <span data-header-markup-complete="" aria-hidden="true" />
        </div>
    );
};

export default forwardRef(Header);
